import { api, type AssociationType } from '@/portal-api.definition';
import { type CaptchaState, useNosIdMutation } from './base.hooks';
import { ActionType, ErrorCodes, useExecuteRecaptcha } from '@nosinovacao/nosid-mfe-common';

export function useGetAccountContacts() {
  return useNosIdMutation({
    mutationFn: ({ type, value }: { type: AssociationType; value: string }) =>
      api.get('/api/v1/account-association', {
        search: {
          Contact: value,
          Type: type,
        },
      }),
    ignoreLogging: true,
    ignoreSnackbarForErrors: [ErrorCodes.PersonNotFound],
  });
}

export function useValidateAccountAssociationContact() {
  return useNosIdMutation({
    mutationFn: ({ type, value }: { type: AssociationType; value: string }) =>
      api
        .get('/api/v1/account-association/is-contact-valid', {
          search: {
            Contact: value,
            Type: type,
          },
        })
        .then(),
    ignoreLogging: true,
    ignoreSnackbarForErrors: ['*'],
  });
}

export function useSendAccountAssociationCode() {
  const executeRecaptcha = useExecuteRecaptcha();
  return useNosIdMutation({
    mutationFn: async ({
      captcha,
      contactId,
      targetId,
      type,
    }: {
      captcha: CaptchaState;
      contactId: number | undefined;
      targetId: string;
      type: AssociationType;
    }) => {
      const token = captcha.challenge
        ? captcha.token
        : await executeRecaptcha('account_association');

      return api.post(
        '/api/v1/account-association/send-code-personal-info',
        {
          ContactId: contactId,
          IdentificationType: type,
          TargetId: targetId,
        },
        {
          captcha: { token, challenge: captcha.challenge },
        },
      );
    },
    successLogAction: () => ({ ActionType: ActionType.ResendCode }),
    errorLogAction: () => ({ ActionType: ActionType.ResendCode }),
  });
}

export function useValidateAccountAssociationCode() {
  return useNosIdMutation({
    mutationFn: async (contact: {
      contactId: number | undefined;
      targetId: string;
      code: string;
      type: AssociationType;
    }) =>
      await api.post('/api/v1/account-association/validate-code-personal-info', {
        TargetId: contact.targetId,
        IdentificationType: contact.type,
        ContactId: contact.contactId,
        Code: contact.code,
      }),
    successLogAction: () => ({ ActionType: ActionType.Submit }),
    errorLogAction: () => ({ ActionType: ActionType.Submit }),
  });
}

export function useCommitAssociation() {
  return useNosIdMutation({
    mutationFn: (contact: {
      contactId: number | undefined;
      targetId: string;
      code: string;
      type: AssociationType;
    }) =>
      api.post(
        '/api/v1/account-association',
        {
          TargetId: contact.targetId,
          Code: contact.code,
          ContactId: contact.contactId,
          IdentificationType: contact.type,
        },
        { search: { associationConfirmed: 'true' } },
      ),
    successLogAction: () => ({ ActionType: ActionType.Submit }),
    errorLogAction: () => ({ ActionType: ActionType.Submit }),
    ignoreSnackbarForErrors: [ErrorCodes.InvalidSecurityCode],
  });
}
